import React, { Dispatch, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';

import { Button } from '@kaleyra/emmy';
import logo from '../../assets/logo.png';
import logoIcon from '../../assets/logo_small.png';
import { REDIRECT_URL, CHECK_COOKIE_INTERVAL, REACT_APP_DISABLE_VOICE } from '../../base';
import ChameleonPackage from '../../components/chameleonPackage';
import KycBanner from '../../components/kycBanner';
import SpriteSvg from '../../components/spriteSvg';
import { TabItemPropsType } from '../../components/common/atoms/TabItem';
import UserProfile from '../../components/common/containers/UserProfile';
import Balance from '../../components/common/containers/Balance';
import WabaId from '../../components/common/molecules/WabaIdDropDown/WabaId';
import * as LeftSideBar from '../../components/common/organisms/LeftSideBar';
import { RightItem } from '../../components/common/organisms/TopBar';
import AppTemplate from '../../components/common/templates/AppTemplate';
import C2CModal from '../../components/C2CModal/containers/C2CModal';
import cfg from '../../config.json';
import * as utils from '../../utils';
import * as urls from '../../urls';
import { KYCStatus } from '../../interface';
import { IStateType } from '../../store/models/root.interface';
import { openC2CModal } from '../../store/actions/C2CModal.action';

import { SidebarMenuProps } from '../../components/common/KDSLeftSideBar/KDSLeftSideBar';
import './FullApp.scss';

type AppTemplatePropsType = {
	kycStatus: KYCStatus;
	tabs: Array<Omit<TabItemPropsType, 'onClick'>>;
	sidebarMenu: SidebarMenuProps;
	backToParentAccount: (data: any, callBack: any) => void;
	onLoad: () => void;
	requestLogout: () => void;
};

const KYCBannerStatus = [KYCStatus.newKYC, KYCStatus.failed, KYCStatus.pending];

const getIcons = (href: string) => {
	return (
		<svg className={'sidebarNav'}>
			<use xlinkHref={href}></use>
		</svg>
	);
};

const getLeftClassName = (hasKycBanner: boolean) => {
	if (hasKycBanner && !utils.getCookie('is_parent_as_child_session'))
		return 'withKycBannerMain';
	if (utils.getCookie('is_parent_as_child_session') && hasKycBanner)
		return 'withKycBannerWithBackToMain';
	if (utils.getCookie('is_parent_as_child_session') && !hasKycBanner)
		return 'withBackToMain';
	return 'withoutKycBanner';
};

const getContentClassName = (hasKycBanner: boolean) => {
	if (hasKycBanner && !utils.getCookie('is_parent_as_child_session'))
		return 'contentWithPadding';
	if (utils.getCookie('is_parent_as_child_session') && hasKycBanner)
		return 'contentWithPadding1';
	if (utils.getCookie('is_parent_as_child_session') && !hasKycBanner)
		return 'contentWithPadding2';
	return '';
};

const AppSetup: React.FC<AppTemplatePropsType> = ({
	children,
	kycStatus,
	tabs,
	sidebarMenu,
	backToParentAccount,
	onLoad,
	requestLogout,
}) => {
	const { t } = useTranslation();
	const dispatch: Dispatch<any> = useDispatch();
	const { data } = useSelector((state: IStateType) => state.C2CGlobalOption);
	const { showModal } = useSelector((state: IStateType) => state.C2CModal);
	const profile = useSelector((state: IStateType) => state.profile);
	const kaleyra_role = profile?.fullProfile?.kaleyra_role || '';
	const { isPrepaid } = useSelector((state: IStateType) => state.balance);
	const { isWhatsappPage } = useSelector((state: IStateType) => state.wabaId);
	const { isKDSPage, pageName } = useSelector(
		(state: IStateType) => state.kdsView
	);

	const billingUrl =
		REDIRECT_URL +
		(isPrepaid === 0 ? cfg.BILLLING_URL : cfg.BILLLING_USAGE_URL);
	const { kyc_status } = profile.fullProfile;

	let PROD: boolean = false;
	if (window.location.hostname.includes('kaleyra')) {
		PROD = true;
	}

	useEffect(() => {
		function makeApiCall() {
			Axios.get(`${window.location.origin}/api/v1/checkcookie`, { headers: { kaleyra_role: kaleyra_role } })
				.then((res: any) => { })
				.catch((err: any) => {
					console.log(err);
					requestLogout();
				});
		}
		setInterval(makeApiCall, CHECK_COOKIE_INTERVAL);
	}, []);

	const Logo: LeftSideBar.LogoType = {
		image: logo,
		icon: logoIcon,
		url: utils.defaultRedirectTo(),
	};

	const hasKycBanner = KYCBannerStatus.includes(kycStatus);

	const leftClassName = getLeftClassName(hasKycBanner);
	const contentClassName = getContentClassName(hasKycBanner);

	const rightItems = [
		<RightItem key={'balance'}>
			<Balance />
		</RightItem>,
		<RightItem key={'userProfile'}>
			<UserProfile />
		</RightItem>,
	];
	
	if (!REACT_APP_DISABLE_VOICE && parseInt(data) === 1 && !isWhatsappPage && ((!utils.checkKaleyraExpertDefaultAccount() ) && (!utils.checkSupportRole()))) {
		rightItems.unshift(
			<div className="flexCenter" key={'newConv'}>
				<RightItem>
					<Button
						type="secondary"
						small
						onClick={() => dispatch(openC2CModal('C2C'))}
					>
						{t('click2call')}
					</Button>
				</RightItem>
			</div>
		);
	}

	if (isWhatsappPage) {
		rightItems.unshift(
			<div className="flexCenter" key={'wabaDropDown'}>
				<RightItem key={'wabaId'}>
					<WabaId />
				</RightItem>
			</div>
		);
	}

	const kycCheck = () => {
		if (kyc_status === 'APPROVED' || kyc_status === 'AUTO-APPROVED') {
			return true;
		} else return false;
	};

	const leftSideBarMenuItems = useMemo(
		() => [
			utils.checkEURegionFeatures('dashboard') &&
				utils.checkPrivileges('dashboard', 'main') &&
				utils.checkEnabledChannel(['sms', 'voice']) ? (
				<LeftSideBar.MenuItem
					key={'dashboard'}
					label={t('tabDashboard')}
					value={urls.dashboard({ accessType: '' })}
					icon={getIcons('#ka-menu-dashboard')}
				/>
			) : null,
			utils.checkEURegionFeatures('campaigns') &&
				utils.checkPrivileges('campaigns', 'main') ? (
				<LeftSideBar.MenuItem
					key={'campaign'}
					label={t('tabCampaigns')}
					value={urls.campaigns()}
					icon={getIcons('#ka-menu-campaign')}
				/>
			) : null,
			utils.checkEURegionFeatures('directory') &&
				utils.checkPrivileges('directory', 'main') ? (
				<LeftSideBar.MenuItem
					key={'directory'}
					label={t('tabDirectory')}
					value={REDIRECT_URL + cfg.DIRECTORY_URL}
					icon={getIcons('#ka-menu-directory')}
					nodeType={'anchor'}
				/>
			) : null,
			utils.checkEURegionFeatures('channels') &&
				utils.checkPrivileges('channels', 'main') ? (
				<LeftSideBar.MenuItem
					key={'channels'}
					label={t('tabChannels')}
					value={urls.channels({})}
					icon={getIcons('#ka-menu-channels')}
				/>
			) : null,
			utils.checkEURegionFeatures('flowBuilder') &&
				utils.checkEnabledChannel(['voice']) &&
				utils.checkPrivileges('flow_builder', 'main') ? (
				utils.checkPermissions('FLOWBUILDER_VIEW') ? (
					<LeftSideBar.MenuItem
						key={'flowBuilder'}
						label={t('tabFlowBuilder')}
						value={urls.flowBuilder({})}
						icon={getIcons('#ka-menu-flow-builder')}
					/>
				) : null
			) : null,
			utils.checkEURegionFeatures('numbers') &&
				utils.checkEnabledChannel(['voice', 'sms']) &&
				utils.checkPrivileges('numbers', 'main') ? (
				utils.checkPermissions('NUMBERS_VIEW') ? (
					<LeftSideBar.MenuItem
						key={'numbers'}
						label={t('tabNumbers')}
						value={urls.v1NumbersManage()}
						icon={getIcons('#ka-menu-numbers')}
					/>
				) : null
			) : null,
			utils.checkEURegionFeatures('verify') &&
				utils.checkEnabledChannel(['sms']) &&
				utils.checkPrivileges('verify', 'main') ? (
				<LeftSideBar.MenuItem
					key={'verify'}
					label={t('tabVerify')}
					value={urls.verify({})}
					icon={getIcons('#ka-menu-verify')}
				/>
			) : null,
			utils.checkEURegionFeatures('chatBot') &&
				utils.checkPrivileges('chatbot', 'main') &&
				utils.checkChatbotPrivileges() ? (
				kycCheck() ? (
					<LeftSideBar.MenuItem
						key={'chatBot'}
						label={t('chatbotMenu')}
						value={urls.ChatbotV2({})}
						icon={getIcons('#ka-menu-chatbot')}
					/>
				) : null
			) : null,
			utils.checkEURegionFeatures('conversation') &&
				utils.checkEnabledChannel(['whatsapp']) &&
				utils.checkIsConversationEnabled() &&
				utils.checkPrivileges('conversations', 'main') ? (
				<LeftSideBar.MenuItem
					key={'conversation'}
					label={t('tabConversation')}
					value={urls.conversations({})}
					icon={getIcons('#ka-menu-conversation')}
				/>
			) : null,
			utils.checkEURegionFeatures('urlShortener') &&
				utils.checkEnabledChannel(['sms']) &&
				utils.checkPrivileges('url_shortner', 'main') ? (
				<LeftSideBar.MenuItem
					key={'urlShortener'}
					label={t('tabURLShortener')}
					value={urls.urlShortener({})}
					icon={getIcons('#ka-menu-urlShortener')}
				/>
			) : null,
			utils.checkEURegionFeatures('subaccounts') &&
				utils.checkPrivileges('subaccount', 'main') ? (
				<LeftSideBar.MenuItem
					key={'subaccounts'}
					label={t('KLR02518')}
					value={urls.subAccountsManagement({})}
					icon={getIcons('#ka-menu-subaccount')}
				/>
			) : null,
			utils.checkFailoverAccess() &&
				utils.checkPrivileges('failover', 'main') &&
				utils.checkEnabledChannel(['sms']) ? (
				<LeftSideBar.MenuItem
					key={'failover'}
					label={t('KLR03060')}
					value={urls.failover({})}
					icon={getIcons('#ka-menu-failover')}
				/>
			) : null,
			utils.checkPrivileges('fallback', 'main') ? <LeftSideBar.MenuItem
				key={'fallback'}
				label={t('fallbackMain')}
				value={urls.fallback({})}
				icon={getIcons('#ka-menu-failover')}
			/>: null
			,
		],
		[]
	);

	const bottomNodes = useMemo(
		() => [
			utils.checkEURegionFeatures('developers') &&
				!utils.checkForSpecificCompanyId('child') &&
				utils.checkPrivileges('developers', 'main') ? (
				<LeftSideBar.MenuItem
					key={'developers'}
					label={t(`tabDevelopers`)}
					value={urls.v1Developers({})}
					icon={getIcons('#ka-menu-developers')}
				/>
			) : null,
			utils.checkEURegionFeatures('billing') &&
				utils.checkPrivileges('billing', 'main') ? (
				<LeftSideBar.MenuItem
					key={'billing'}
					label={t(`tabBilling`)}
					value={
						utils.checkPrivileges('billing', 'topup')
							? billingUrl
							: urls.v1Billing({ tab: 'usage' })
					}
					icon={getIcons('#ka-menu-billing')}
					nodeType={
						utils.checkPrivileges('billing', 'topup')
							? 'anchor'
							: undefined
					}
				/>
			) : null,
		],
		[billingUrl]
	);

	return (
		<AppTemplate>
			{PROD && <ChameleonPackage />}
			<SpriteSvg />
			<KycBanner
				backToParentAccount={backToParentAccount}
				onLoad={onLoad}
				requestLogout={requestLogout}
			/>

			{isKDSPage ? (
				<>
					<AppTemplate.KDSLeftSideBar
						logoUrl={Logo.url}
						className={leftClassName}
						menu={sidebarMenu}
						channelName={pageName}
					/>

					<AppTemplate.ContentWrapper
						contentClassNames={contentClassName}
					>
						<AppTemplate.KDSTopBar rightItems={rightItems} />
						{children}
						{showModal && <C2CModal />}
					</AppTemplate.ContentWrapper>
				</>
			) : (
				<>
					<AppTemplate.LeftSideBar
						logo={Logo}
						bottomNodes={bottomNodes}
						classNames={leftClassName}
					>
						{leftSideBarMenuItems}
					</AppTemplate.LeftSideBar>

					<AppTemplate.ContentWrapper
						contentClassNames={contentClassName}
					>
						<AppTemplate.TopBar
							rightItems={rightItems}
							tabs={tabs}
						/>
						{children}
						{showModal && <C2CModal />}
					</AppTemplate.ContentWrapper>
				</>
			)}
		</AppTemplate>
	);
};

export default AppSetup;
